import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductEcosystemSection from "@/components/products-landing/ProductEcosystemSection";
import EnterpriseBenefitsSection from "@/components/products-landing/EnterpriseBenefitsSection";
import DetailedProductCards from "@/components/products-landing/DetailedProductCards";
import CTASection from "@/components/products-landing/CTASection";
import FAQSection from "@/components/products-landing/FAQSection";
import auraXPHero from "@/assets/auraxp-products-hero.jpg";
import aiSecurityFabricBg from "@/assets/ai-security-fabric-bg.jpg";

const ProductsLanding = () => {
  React.useEffect(() => {
    document.title = "Products - Spharaka Networks | AI-Native Cybersecurity Platform";
    
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', 'Discover Spharaka Networks\' suite of AI-native cybersecurity products: Spharaka XDR, AuraXP™, and ISHWAR. Autonomous protection for enterprises, governments, and organizations.');
    document.head.appendChild(metaDescription);

    return () => {
      try {
        document.head.removeChild(metaDescription);
      } catch (e) {}
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Spharaka Security Ecosystem Powered by <span className="text-[#8932C8]">AuraXP</span>
              </h1>
              <p className="text-xl md:text-2xl text-foreground font-semibold">
                AuraXP: The Intelligent Cyber Brain for Autonomous Defense
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Your organization's intelligent cyber brain that continuously learns, adapts, and protects your digital environment from evolving threats.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a 
                  href="https://outlook.office.com/bookwithme/user/3c14c6b9047b45dfb64e4bc9045f29dd@spharaka.com/meetingtype/p0-SnGKD_EqEPlSvi_wdNg2?anonymous&ismsaljsauthenabled&ep=mlink"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-[#8932C8] rounded-lg hover:bg-[#8932C8]/90 transition-all shadow-lg hover:shadow-xl"
                >
                  Book a Demo
                </a>
                <a 
                  href="/aura-technology" 
                  className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-[#8932C8] bg-transparent border-2 border-[#8932C8] rounded-lg hover:bg-[#8932C8]/10 transition-all"
                >
                  Explore AuraXP
                </a>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              {/* Glow effects for blending */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#8932C8]/30 via-transparent to-blue-500/30 blur-3xl -z-10"></div>
              <div className="absolute -inset-4 bg-gradient-to-tr from-purple-500/20 to-orange-500/20 blur-2xl -z-10"></div>
              
              <img 
                src={auraXPHero}
                alt="AuraXP AI-powered cybersecurity intelligence - autonomous threat detection and response"
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section with Background */}
      <section 
        className="relative py-12 md:py-16 overflow-hidden"
        style={{
          backgroundImage: `url(${aiSecurityFabricBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark overlay for text visibility */}
        <div className="absolute inset-0 bg-black/70"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Spharaka's AI Security Fabric
            </h2>
            
            <p className="text-lg text-white leading-relaxed">
              AuraXP is Spharaka's AI Security Fabric, the core intelligence that powers autonomous cyber defense across the Spharaka ecosystem. It acts as your organization's intelligent cyber brain, continuously learning, adapting, and protecting your digital environment from evolving threats.
            </p>
            <p className="text-lg text-white leading-relaxed">
              Unlike traditional security tools, AuraXP doesn't just detect and respond, it thinks, remembers, and evolves. Acting as the Organizational Memory of your Security Environment, AuraXP builds contextual intelligence from every alert, event, and behavior, driving faster and more accurate threat responses.
            </p>
            
            <div className="pt-8">
              <h3 className="text-2xl font-bold mb-6 text-white">With AuraXP, your enterprise gains:</h3>
              <ul className="space-y-3 text-lg text-white">
                <li className="flex items-start">
                  <span className="mr-3 text-[#8932C8]">•</span>
                  <span>Unified Threat Intelligence Fabric that connects data across all security layers</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-[#8932C8]">•</span>
                  <span>AI-Powered Autonomous Response Engine for instant mitigation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-[#8932C8]">•</span>
                  <span>Continuous Learning Memory that improves defenses with every event</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-[#8932C8]">•</span>
                  <span>Integrated Security Operations across XDR, EDR, Zero Trust, and App Locker</span>
                </li>
              </ul>
            </div>

            <p className="text-lg text-white leading-relaxed pt-6 font-semibold">
              AuraXP transforms security from reactive monitoring to proactive, intelligent cyber defense.
            </p>
          </div>
        </div>
      </section>

      {/* Defense in Depth Section */}
      <section className="container mx-auto px-4 py-16 md:py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Defense in Depth, The Spharaka AI Security Stack
          </h2>
          <p className="text-lg text-foreground leading-relaxed mb-12">
            The Spharaka Defense-in-Depth architecture combines multiple layers of protection, all powered by the intelligence of AuraXP. From endpoints and networks to applications, every component operates as a unified, self-learning ecosystem designed for comprehensive enterprise cybersecurity.
          </p>

          {/* AuraXP Core Intelligence Container */}
          <div className="border-4 border-[#8932C8] rounded-2xl p-8 md:p-12 bg-background/50 backdrop-blur-sm">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                The Core Intelligence: <span className="text-[#8932C8]">AuraXP</span>
              </h3>
              <p className="text-lg text-foreground leading-relaxed max-w-4xl mx-auto">
                AuraXP serves as the AI-powered security brain orchestrating detection, response, and adaptive learning across your entire digital infrastructure. It delivers context-aware insights, automated remediation, and a real-time understanding of your organization's threat landscape.
              </p>
            </div>

            {/* Four Solutions Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Spharaka XDR */}
              <div className="border-2 border-[#8932C8]/30 rounded-xl p-6 bg-background hover:border-[#8932C8] transition-all">
                <h4 className="text-xl md:text-2xl font-bold mb-4">
                  <span className="text-[#8932C8]">1.</span> Detection and Response: Spharaka XDR
                </h4>
                <p className="text-base text-foreground leading-relaxed mb-4">
                  Spharaka XDR (Extended Detection and Response) expands visibility across endpoints, networks, users, and cloud workloads. It leverages AuraXP's advanced analytics to identify complex attack patterns and autonomously respond to threats.
                </p>
                <ul className="space-y-2 text-base text-foreground">
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Unified detection across domains</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Automated threat investigation and remediation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>AI-driven incident prioritization</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Correlation of telemetry for full attack visibility</span>
                  </li>
                </ul>
              </div>

              {/* Spharaka EDR and UEBA */}
              <div className="border-2 border-[#8932C8]/30 rounded-xl p-6 bg-background hover:border-[#8932C8] transition-all">
                <h4 className="text-xl md:text-2xl font-bold mb-4">
                  <span className="text-[#8932C8]">2.</span> Endpoint Security: Spharaka EDR and UEBA
                </h4>
                <p className="text-base text-foreground leading-relaxed mb-4">
                  Spharaka EDR (Endpoint Detection and Response) and UEBA (User and Entity Behavior Analytics) deliver AI-driven endpoint protection and behavioral threat analytics. These platforms continuously monitor user and device activities, detecting insider threats and advanced malware with precision.
                </p>
                <ul className="space-y-2 text-base text-foreground">
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Real-time endpoint protection and monitoring</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>AI-powered behavioral analytics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Autonomous containment and remediation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Adaptive learning for evolving threat patterns</span>
                  </li>
                </ul>
              </div>

              {/* Spharaka Zero Trust */}
              <div className="border-2 border-[#8932C8]/30 rounded-xl p-6 bg-background hover:border-[#8932C8] transition-all">
                <h4 className="text-xl md:text-2xl font-bold mb-4">
                  <span className="text-[#8932C8]">3.</span> Network Security: Spharaka Zero Trust
                </h4>
                <p className="text-base text-foreground leading-relaxed mb-4">
                  Spharaka Zero Trust redefines perimeter security by enforcing continuous verification and least-privilege access. Every user, device, and connection request is dynamically authenticated using AuraXP's contextual intelligence.
                </p>
                <ul className="space-y-2 text-base text-foreground">
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Zero Trust network access (ZTNA)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Micro-segmentation for secure zones</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Continuous identity and session validation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>AI-driven access control and policy enforcement</span>
                  </li>
                </ul>
              </div>

              {/* Spharaka App Locker */}
              <div className="border-2 border-[#8932C8]/30 rounded-xl p-6 bg-background hover:border-[#8932C8] transition-all">
                <h4 className="text-xl md:text-2xl font-bold mb-4">
                  <span className="text-[#8932C8]">4.</span> Application Security: Spharaka App Locker
                </h4>
                <p className="text-base text-foreground leading-relaxed mb-4">
                  Spharaka App Locker protects critical business applications from tampering, unauthorized execution, and misuse. It ensures that only verified and trusted software can run, reducing your application attack surface dramatically.
                </p>
                <ul className="space-y-2 text-base text-foreground">
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Application control and whitelisting</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Real-time monitoring of application behavior</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Autonomous threat prevention for apps</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#8932C8]">•</span>
                    <span>Integrated alerting and enforcement</span>
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-center text-base text-muted-foreground mt-8 italic">
              All solutions powered and orchestrated by AuraXP's AI intelligence
            </p>
          </div>
        </div>
      </section>

      {/* Unified Intelligence Section */}
      <section className="container mx-auto px-4 py-16 md:py-20 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
            Unified by Intelligence. Strengthened by Memory.
          </h2>
          <p className="text-lg text-foreground leading-relaxed mb-6">
            Every Spharaka security platform: XDR, EDR, UEBA, Zero Trust, and App Locker; is powered, connected, and continuously optimized by AuraXP. Together, they create an autonomous, adaptive, and memory-driven security ecosystem that protects your organization end to end.
          </p>
          <p className="text-lg text-foreground leading-relaxed">
            Spharaka's AI Cyber Defense Architecture ensures complete visibility, rapid response, and continuous learning, making your cybersecurity posture not just resilient, but intelligent.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-16 md:py-20 overflow-hidden bg-gradient-to-br from-[#8932C8]/10 via-background to-[#8932C8]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-[#8932C8]">AuraXP</span>, Spharaka's Autonomous Intelligence Platform
            </h2>
            <p className="text-xl text-foreground leading-relaxed mb-8">
              Your organization's security, powered by memory, intelligence, and AI-driven protection.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://outlook.office.com/bookwithme/user/3c14c6b9047b45dfb64e4bc9045f29dd@spharaka.com/meetingtype/p0-SnGKD_EqEPlSvi_wdNg2?anonymous&ismsaljsauthenabled&ep=mlink"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-[#8932C8] rounded-lg hover:bg-[#8932C8]/90 transition-all shadow-lg hover:shadow-xl"
              >
                Book a Demo
              </a>
              <a 
                href="/aura-technology" 
                className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-[#8932C8] bg-transparent border-2 border-[#8932C8] rounded-lg hover:bg-[#8932C8]/10 transition-all"
              >
                Explore AuraXP
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      <Footer />
    </div>
  );
};

export default ProductsLanding;
